import React, { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import enGB from "date-fns/locale/en-GB";
import { HiOutlineCalendarDays, HiOutlineChevronDown } from "react-icons/hi2";
import { changeDateToYearMonthDay } from "Utils/utils";
import {
  formatTimesheetRangeLabel,
  toCalendarDate,
} from "./timesheetFilterDate";
import "components/common/DropDown/ThemedDropdown.css";

enGB.options = { ...enGB.options, weekStartsOn: 1 };
registerLocale("en-GB", enGB);

/** Same chrome and type as the participant filter beside it. */
const TRIGGER_CLASS =
  "flex h-[34px] w-full min-w-0 items-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-[#F8FAFC] px-[11px] box-border text-left text-[13px] font-semibold leading-none text-[#0F172A] shadow-none transition-colors hover:border-[#C5D4EA] focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-[#4A6BB6]/30";

const LABEL_CLASS =
  "min-w-0 flex-1 truncate text-[13px] font-semibold leading-none text-[#0F172A]";

const TimesheetFilterDateRange = ({
  fromDate,
  toDate,
  handleChange,
  setCheckboxes,
  setPage,
  isAllreadySelectedTimesheet,
  maxDate,
  setSelectedFromDate,
  portalId,
  fullWidth = false,
}) => {
  const datePickerRef = useRef(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const fromCalendar = toCalendarDate(fromDate);
  const toCalendar = toCalendarDate(toDate);
  const fromTime = fromCalendar ? fromCalendar.getTime() : null;
  const toTime = toCalendar ? toCalendar.getTime() : null;

  // Sync from the parent only when the committed range changes. A new Date
  // object on every render must not wipe the start date the user just clicked
  // while they are still choosing the end date.
  useEffect(() => {
    setDateRange((current) => {
      if (calendarOpen && current[0] && !current[1]) return current;
      const currentFrom = current[0] ? current[0].getTime() : null;
      const currentTo = current[1] ? current[1].getTime() : null;
      if (currentFrom === fromTime && currentTo === toTime) return current;
      return [
        fromTime != null ? new Date(fromTime) : null,
        toTime != null ? new Date(toTime) : null,
      ];
    });
  }, [fromTime, toTime, calendarOpen]);

  const [startDate, endDate] = dateRange;
  const label = formatTimesheetRangeLabel(startDate, endDate);

  const CustomInput = React.forwardRef(({ onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      className={
        fullWidth
          ? `themed-dropdown-trigger flex w-full items-center gap-2 px-[11px] text-left text-[13px] font-semibold leading-none text-[#0F172A] ${
              calendarOpen ? "is-open" : ""
            }`
          : `${TRIGGER_CLASS} md:w-auto md:min-w-[240px] md:max-w-[300px]`
      }
    >
      <HiOutlineCalendarDays
        className={`${fullWidth ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0 text-[#64748B]`}
        aria-hidden
      />
      <span className={LABEL_CLASS}>{label}</span>
      <HiOutlineChevronDown
        className={`${fullWidth ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0 text-[#94A3B8]`}
        aria-hidden
      />
    </button>
  ));

  CustomInput.displayName = "TimesheetFilterDateRangeInput";

  return (
    <div className={fullWidth ? "w-full min-w-0" : "w-full min-w-0 md:w-auto md:shrink-0"}>
      <DatePicker
        ref={datePickerRef}
        wrapperClassName={
          fullWidth
            ? "timesheet-filter-date-picker timesheet-filter-date-picker--full w-full"
            : "timesheet-filter-date-picker w-full md:w-auto"
        }
        selectsRange
        startDate={startDate}
        endDate={endDate}
        openToDate={startDate || new Date()}
        onChange={(selectedDates) => {
          if (setSelectedFromDate && selectedDates[0]) {
            setSelectedFromDate(selectedDates[0]);
          } else if (setSelectedFromDate && !selectedDates[0]) {
            setSelectedFromDate(null);
          }

          if (
            selectedDates &&
            selectedDates.length > 0 &&
            !selectedDates[0] &&
            !selectedDates[1]
          ) {
            setDateRange([null, null]);
            return;
          }

          if (selectedDates && selectedDates[0] && selectedDates[1]) {
            if (isAllreadySelectedTimesheet && setCheckboxes) {
              setCheckboxes([]);
            }
            setDateRange(selectedDates);
            if (setPage) setPage(1);
            handleChange({
              fromDate: changeDateToYearMonthDay(selectedDates[0]),
              toDate: changeDateToYearMonthDay(selectedDates[1]),
            });
          } else {
            setDateRange(selectedDates);
          }
        }}
        maxDate={maxDate}
        onCalendarOpen={() => setCalendarOpen(true)}
        onCalendarClose={() => setCalendarOpen(false)}
        locale="en-GB"
        dateFormat="d MMM yyyy"
        customInput={<CustomInput />}
        popperPlacement="bottom-start"
        popperClassName="timesheet-filter-date-popper"
        portalId={portalId}
        popperProps={portalId ? { strategy: "fixed" } : undefined}
        popperModifiers={[
          { name: "flip", options: { fallbackPlacements: ["top-start", "bottom-start"] } },
          { name: "preventOverflow", options: { boundary: "viewport", padding: 8, altAxis: true } },
        ]}
      />
    </div>
  );
};

export default TimesheetFilterDateRange;
